// File size display component showing original vs processed size
import React from 'react';
import { Box, Flex, VStack, HStack, Text } from '@chakra-ui/react';
import { Image as ImageIcon } from 'lucide-react';
import { ImageItem } from '../types';

interface FileSizeDisplayProps {
  currentImage: ImageItem | undefined;
  savedBytes: number;
  savedPercent: number;
  formatFileSize: (bytes: number) => string;
}

export const FileSizeDisplay: React.FC<FileSizeDisplayProps> = ({
  currentImage,
  savedBytes,
  savedPercent,
  formatFileSize,
}) => {
  return (
    <Box
      bg="background.secondary"
      borderRadius="xl"
      p={5}
      border="1px solid"
      borderColor="border.default"
    >
      <HStack mb={4}>
        <ImageIcon size={14} color="#A71C29" />
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="text.secondary"
          textTransform="uppercase"
          letterSpacing="wider"
          fontFamily="secondary"
        >
          File Size
        </Text>
      </HStack>

      <Flex justify="space-around" align="center">
        <VStack spacing={1}>
          <Text fontSize="xs" color="text.secondary" fontFamily="secondary">
            Original
          </Text>
          <Text fontSize="md" fontWeight="semibold" color="text.primary" fontFamily="secondary">
            {formatFileSize(currentImage?.file.size || 0)}
          </Text>
        </VStack>
        <Text fontSize="xl" color="text.secondary">
          →
        </Text>
        <VStack spacing={1}>
          <Text fontSize="xs" color="text.secondary" fontFamily="secondary">
            Processed
          </Text>
          <Text
            fontSize="md"
            fontWeight="semibold"
            color={currentImage?.processedSize ? 'brand.primary.DEFAULT' : 'text.disable'}
            fontFamily="secondary"
          >
            {currentImage?.processedSize ? formatFileSize(currentImage.processedSize) : '—'}
          </Text>
        </VStack>
      </Flex>

      {currentImage?.processedSize && savedBytes > 0 && (
        <Box mt={4} p={2} bg="status.success" borderRadius="lg" textAlign="center">
          <Text fontSize="sm" color="white" fontWeight="medium" fontFamily="secondary">
            ↓ Saved {formatFileSize(savedBytes)} ({savedPercent}% smaller)
          </Text>
        </Box>
      )}

      {currentImage?.processedSize && savedBytes < 0 && (
        <Box mt={4} p={2} bg="status.warning" borderRadius="lg" textAlign="center">
          <Text fontSize="sm" color="white" fontWeight="medium" fontFamily="secondary">
            File size increased by {formatFileSize(Math.abs(savedBytes))}
          </Text>
        </Box>
      )}
    </Box>
  );
};
